// Copyright © 2023 Ory Corp
// SPDX-License-Identifier: Apache-2.0
// This file was copied and modified from:
// https://github.com/ory/elements/blob/main/examples/react-spa/src/sdk.ts
// Ory custom UI elements documentation:
// https://www.ory.sh/docs/kratos/bring-your-own-ui/custom-ui-ory-elements

import { Configuration, FrontendApi, ResponseError } from "@ory/client-fetch";

const basePath = import.meta.env.VITE_APP_ORY_AUTH_ENDPOINT;

export const sdk = new FrontendApi(
  new Configuration({
    basePath,
    credentials: "include",
    headers: {
      Accept: "application/json",
    },
  })
);

// Ory configuration for elements-react components.
export const oryConfig = {
  sdk: {
    url: basePath,
    options: {
      credentials: "include" as RequestCredentials,
    },
  },
  project: {
    name: "Reboot",
    registration_enabled: true,
    recovery_enabled: true,
    verification_enabled: false,
    login_ui_url: basePath + "/login",
    registration_ui_url: basePath + "/registration",
    recovery_ui_url: basePath + "/recovery",
    verification_ui_url: basePath + "/verification",
    settings_ui_url: basePath + "/settings",
    error_ui_url: basePath + "/error",
    default_locale: "en",
    default_redirect_url: "/",
    locale_behavior: "respect_accept_language" as const,
  },
};

/**
 * @param getFlow - Should be function to load a flow make it visible
 * @param setFlow - Update flow data to view
 * @param defaultNav - Default navigate target for errors
 * @param fatalToDash - When true and error can not be handled, redirect to
 *                      dashboard, else rethrow error
 */
export function createSdkErrorHandler(
  navigate: (path: string, options?: { replace?: boolean }) => void,
  getFlow: ((flowId: string) => Promise<void>) | undefined,
  setFlow: ((value: any) => void) | undefined,
  defaultNav: string | undefined,
  fatalToDash = false
) {
  return async (error: unknown): Promise<void> => {
    if (!(error instanceof ResponseError)) {
      if (fatalToDash) {
        navigate("/", { replace: true });
        return;
      }
      throw error;
    }

    const response = error.response;
    const status = response.status;

    let responseData: Record<string, unknown> = {};
    try {
      responseData = await response.json();
    } catch {
      // Response may not have JSON body.
    }

    switch (status) {
      case 400: {
        const errorId = (responseData.error as Record<string, unknown>)?.id;
        if (errorId === "session_already_available") {
          // Only redirect if we're still on an auth page.
          const currentPath = window.location.pathname;
          if (
            currentPath !== "/signup" &&
            currentPath !== "/login" &&
            currentPath !== "/recovery"
          ) {
            return;
          }

          // If there's a return_to parameter, redirect there instead of "/".
          const returnTo = new URLSearchParams(window.location.search).get(
            "return_to"
          );
          if (returnTo) {
            const returnUrl = new URL(returnTo, window.location.origin);
            navigate(returnUrl.pathname + returnUrl.search, { replace: true });
          } else {
            navigate("/", { replace: true });
          }
          return;
        }
        if (setFlow !== undefined) {
          setFlow(responseData);
          return;
        }
        break;
      }
      case 401: {
        // Don't navigate if we're already on the target page.
        const currentPath = window.location.pathname;
        if (defaultNav && currentPath !== defaultNav) {
          navigate(defaultNav, { replace: true });
        }
        return;
      }
      case 403: {
        const errorId = (responseData.error as Record<string, unknown>)?.id;
        if (errorId === "session_aal2_required") {
          navigate(`/${defaultNav}?aal2=true`, { replace: true });
          return;
        }

        if (
          errorId === "session_refresh_required" &&
          responseData.redirect_browser_to
        ) {
          window.location.href = responseData.redirect_browser_to as string;
          return;
        }
        break;
      }
      case 404: {
        if (defaultNav !== undefined) {
          const errorMsg = {
            data: responseData,
            status,
            url: window.location.href,
          };

          navigate(
            `/error?error=${encodeURIComponent(JSON.stringify(errorMsg))}`,
            { replace: true }
          );
          return;
        }
        break;
      }
      case 410: {
        const useFlowId = responseData.use_flow_id as string | undefined;
        if (getFlow !== undefined && useFlowId !== undefined) {
          try {
            await getFlow(useFlowId);
          } catch {
            if (defaultNav !== undefined) {
              navigate(defaultNav, { replace: true });
            }
          }
          return;
        } else if (defaultNav !== undefined) {
          navigate(defaultNav, { replace: true });
          return;
        }
        break;
      }
      case 422: {
        const redirectTo = responseData.redirect_browser_to as
          | string
          | undefined;
        if (redirectTo !== undefined) {
          const currentUrl = new URL(window.location.href);
          const redirect = new URL(redirectTo, window.location.origin);

          if (currentUrl.pathname !== redirect.pathname) {
            redirect.pathname = redirect.pathname.replace("/ui", "");
            navigate(redirect.pathname + redirect.search, { replace: true });
            return;
          }

          const flowId = redirect.searchParams.get("flow");

          if (flowId != null && getFlow !== undefined) {
            try {
              await getFlow(flowId);
            } catch {
              if (defaultNav !== undefined) {
                navigate(defaultNav, { replace: true });
              }
            }
            return;
          } else {
            window.location.href = redirectTo;
            return;
          }
        }
      }
    }

    if (fatalToDash) {
      navigate("/", { replace: true });
      return;
    }

    throw error;
  };
}
